/**
 * FEEDBACK COMPONENT REGISTRY
 * ===========================
 * Which feedback definition each screen uses.
 *
 *   ADD A ROW HERE when you create a definition for a specific screen.
 *
 * ---------------------------------------------------------------------------
 * READ THIS BEFORE ADDING A ROW
 *
 * This file is an OVERRIDE, not the wiring. A screen with no row here still
 * gets a form — the server falls back on its own:
 *
 *     this project + this screen  ->  this project, any screen  ->  global default
 *
 * So the registry is never required for feedback to work. What it buys you is
 * PINNING: naming an exact definition `key` guarantees which one a screen opens,
 * regardless of what else exists or what someone changes in Feedback Master.
 *
 * The cost is that it is a second list. The definitions themselves live in the
 * database; this file duplicates part of that, and a row that names a key which
 * has been renamed, deactivated or deleted will simply stop matching. When that
 * happens the screen falls back to the generic form silently — nothing errors.
 * `verifyFeedbackRegistry()` below exists to catch exactly that.
 *
 * If you would rather not maintain it: delete the rows. Setting the screen on
 * the definition itself (Feedback Master -> "Which screen?") achieves the same
 * routing with nothing to keep in sync.
 * ---------------------------------------------------------------------------
 *
 * `screen` values are nav collection ids — the same codes the definition form's
 * screen picker offers. See screen-code.ts / SCREEN_CODES for the full list.
 */

export interface FeedbackComponent {
  /** Nav collection id, e.g. 'errormaster'. Must match SCREEN_CODES. */
  screen: string;
  /** Exact `key` of the definition in Feedback Master. */
  definitionKey: string;
  /** Why this screen has its own form — for whoever reads this next. */
  note?: string;
}

export const FEEDBACK_COMPONENTS: FeedbackComponent[] = [
  // ---------------------------------------------------------------------------
  // Example rows — replace with your own. Both definitions are described in
  // FEEDBACK-SCREEN-SETUP.pdf.
  //
  // { screen: 'errormaster', definitionKey: 'FDBK.SCREEN.ERRORMASTER.ISSUE',
  //   note: 'Issue report: what went wrong, then the details.' },
  // { screen: 'units', definitionKey: 'FDBK.SCREEN.UNITS.PULSE',
  //   note: 'Two-tap pulse — thumb and stars, nothing required.' },
  // ---------------------------------------------------------------------------
];

/** Index built once. Last row wins if a screen is listed twice. */
const BY_SCREEN: Record<string, FeedbackComponent> = Object.fromEntries(
  FEEDBACK_COMPONENTS.map((c) => [c.screen, c]),
);

/**
 * The definition key pinned to a screen, or null to let the server resolve.
 * Null is the normal case and is not a problem.
 */
export function definitionKeyForScreen(screen: string | null): string | null {
  if (!screen) return null;
  return BY_SCREEN[screen]?.definitionKey ?? null;
}

/**
 * Rows whose `screen` is not a real screen code.
 *
 * A typo here fails silently — the screen just falls back to the generic form —
 * so this is checked at startup in dev and logged. It cannot check the
 * definition KEY, which only the server knows; a wrong key shows up as a screen
 * quietly serving the generic form.
 */
export function verifyFeedbackRegistry(validScreenCodes: string[]): string[] {
  const valid = new Set(validScreenCodes);
  return FEEDBACK_COMPONENTS
    .filter((c) => !valid.has(c.screen))
    .map((c) => `${c.screen} (pinned to ${c.definitionKey})`);
}

export default FEEDBACK_COMPONENTS;
